import TextInput from 'components/Common/Form/Input/TextInput'
import { useCallback, useContext, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Select from 'components/Common/Form/Input/Select'
import { getData as getCountryData } from 'country-list'
import AgentHubAddressAutocomplete from './AgentHubAddressAutocomplete'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import type { GooglePlaceDetails } from 'api/search'

const countryNames = getCountryData()
  .map((country) => country.name)
  .sort()

const FullWidthAutocompleteWrapper = styled.div`
  grid-column: 1 / -1;
  margin-bottom: ${rem(16)};
`

function getAddressValue(place: Array<any>, type: string) {
  const addressValue = place.find((component) => {
    return component.types.includes(type)
  })?.longName || ''

  return addressValue
}

function AgentHubAddressInputFields() {
  const intl = useIntl()
  const [cityValue, setCityValue] = useState('')
  const [stateValue, setStateValue] = useState('')
  const [postcodeValue, setPostcodeValue] = useState('')
  const [countryValue, setCountryValue] = useState('')

  const { currentRegionCode } = useContext(GeoContext)
  const isGBRegion = currentRegionCode === 'GB'

  const countyLabel = intl.formatMessage({
    id: '-l1w-ye38buF0jKWm4Lz',
    defaultMessage: 'County',
    description: 'Address input label for county (UK region) in the agent hub portal',
  })
  const stateLabel = intl.formatMessage({
    id: 'kbYah9u6iOc4Gc3TcUOa',
    defaultMessage: 'State or territory',
    description: 'Address input label for state or territory in the agent hub portal',
  })

  const onPlaceSelected = useCallback((searchResult: GooglePlaceDetails) => {
    if (searchResult) {
      setStateValue(getAddressValue(searchResult, isGBRegion ? 'administrative_area_level_2' : 'administrative_area_level_1'))
      setPostcodeValue(getAddressValue(searchResult, 'postal_code'))
      setCityValue(getAddressValue(searchResult, 'locality') || getAddressValue(searchResult, 'administrative_area_level_2'))
      setCountryValue(() => {
        const country = getAddressValue(searchResult, 'country')
        const matchedCountry = countryNames.find((countryName) =>
          countryName.toLowerCase().includes(country.toLowerCase()),
        )
        return matchedCountry || country
      })
    }
  }, [isGBRegion])

  function updateCityValue(event: React.ChangeEvent<HTMLInputElement>) {
    setCityValue(event.target.value)
  }

  function updateStateValue(event: React.ChangeEvent<HTMLInputElement>) {
    setStateValue(event.target.value)
  }

  function updatePostcodeValue(event: React.ChangeEvent<HTMLInputElement>) {
    setPostcodeValue(event.target.value)
  }

  function updateCountryValue(event: React.ChangeEvent<HTMLSelectElement>) {
    setCountryValue(event.target.value)
  }

  return (
    <>
      <FullWidthAutocompleteWrapper>
        <AgentHubAddressAutocomplete
          onSelect={onPlaceSelected}
          inputName="streetAddress"
          label={intl.formatMessage({
            id: 'E0BJ86Gb6dTyvhI4UZGO',
            defaultMessage: 'Street address',
            description: 'Address input label for street address in the agent hub portal',
          })}
          placeholder={intl.formatMessage({
            id: 'fjH3HDy5S5XlxIAQ58S-',
            defaultMessage: 'Search by address',
            description: 'Address autocomplete input placeholder in the search and discovery interface',
          })}
        />
      </FullWidthAutocompleteWrapper>

      <TextInput
        label={intl.formatMessage({
          id: 'dGoddFuAHtBu5-ZuFBI2',
          defaultMessage: 'City or suburb',
          description: 'Address input label for city or suburb in the agent hub portal',
        })}
        name="city"
        placeholder={intl.formatMessage({
          id: 'L-tIqZ2YMHvaCV138IVf',
          defaultMessage: 'Enter city or suburb',
          description: 'Address input placeholder for city or suburb when field is empty',
        })}
        required
        requiredErrorMessage={intl.formatMessage({
          id: 'nxrSQfwvYaaKOC0f1TFh',
          defaultMessage: 'Please enter city or suburb',
          description: 'Address input validation error for missing city or suburb',
        })}
        value={cityValue}
        onChange={updateCityValue}
      />
      <TextInput
        label={isGBRegion ? countyLabel : stateLabel}
        name="stateOrTerritory"
        placeholder={intl.formatMessage({
          id: 'lzfauHzRVTnGFWwMgud0',
          defaultMessage: 'Enter state or territory',
          description: 'Address input placeholder for state or territory in the agent hub portal',
        })}
        required
        requiredErrorMessage={intl.formatMessage({
          id: 'px8iCu7Dk6lBWh2tKCgZ',
          defaultMessage: 'Please enter state or territory',
          description: 'Address input validation error for missing state or territory',
        })}
        value={stateValue}
        onChange={updateStateValue}
      />
      <TextInput
        id="postcode"
        label={intl.formatMessage({
          id: '0eedaGBenbjyaOWpzrZY',
          defaultMessage: 'Postcode',
          description: 'Address input label for postcode in the agent hub portal',
        })}
        type="text"
        name="postcode"
        placeholder={intl.formatMessage({
          id: 'HP5SHVUaCi_yDAckb9lg',
          defaultMessage: 'Enter postcode',
          description: 'Address input placeholder for postcode in the agent hub portal',
        })}
        noValidationSpacing
        pattern="^[^\.$\%\&\(\)\*!,@#]*$"
        invalidErrorMessage={intl.formatMessage({
          id: 'LsbNxIqz_fHdn11Tuoi9',
          defaultMessage: 'Please enter a valid postcode',
          description: 'Address input validation error for invalid postcode',
        })}
        minLength={3}
        maxLength={15}
        required
        autoComplete="postal-code"
        value={postcodeValue}
        onChange={updatePostcodeValue}
      />
      <Select
        label={intl.formatMessage({
          id: 'YUdak0pSLj2BylAM8wXQ',
          defaultMessage: 'Country',
          description: 'Address input label for country in the agent hub portal',
        })}
        name="country"
        placeholder={intl.formatMessage({
          id: 'oqUVQsstYQBNxGln-uaj',
          defaultMessage: 'Select country',
          description: 'Address input placeholder for country selection in the agent hub portal',
        })}
        required
        requiredErrorMessage={intl.formatMessage({
          id: '4P3OZTqg5mYwuR8lX8PS',
          defaultMessage: 'Please select a country',
          description: 'Address input validation error for missing country selection',
        })}
        autoComplete="country-name"
        value={countryValue}
        onChange={updateCountryValue}
      >
        {countryNames.map((countryName) => (
          <option key={countryName} value={countryName}>
            {countryName}
          </option>
        ))}
      </Select>
    </>
  )
}

export default AgentHubAddressInputFields
